import { useTranslation } from 'react-i18next';
import type { VehicleDTO } from '@/api/generated/types';
import { Badge, Tooltip, cn, toneOf, type Option, type Tone } from '@/ui';
import { VEHICLE_STATUSES } from '../shared/labels';

/** Vehicle status names in the active language; codes and colours stay in shared/labels.ts. */
export function useVehicleLabels() {
  const { t } = useTranslation('operations');
  const names = t('vehicleStatus', { returnObjects: true }) as Record<string, string>;
  return {
    status(code: number | null | undefined): { label: string; tone: Tone } | undefined {
      if (code == null) return undefined;
      const s = VEHICLE_STATUSES[code];
      return { label: names[String(code)] ?? s?.label ?? `#${code}`, tone: toneOf(s?.color) };
    },
    /** Every status as options (manual ones settable by hand). */
    statusOptions: Object.entries(VEHICLE_STATUSES).map(([k, s]): Option<number> & { manual: boolean } => ({ value: Number(k), label: names[k] ?? s.label, manual: s.manual })),
  };
}

const SEGMENTS = 5;

/** Battery percent with a five-step traffic-light bar (red under 20, blue under 40, green above). */
export function Battery({ value, label }: { value: number | null | undefined; label?: string }) {
  const { t } = useTranslation('operations');
  if (value == null) return <span className="text-ink-faint">—</span>;
  const fill = value < 20 ? 'bg-danger-ink' : value < 40 ? 'bg-info-ink' : 'bg-success-ink';
  const lit = Math.round((Math.max(0, Math.min(100, value)) / 100) * SEGMENTS);
  return (
    <span
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-label={label ?? t('vehicles.battery')}
      className="inline-flex items-center gap-2"
    >
      <span className="inline-flex gap-0.5" aria-hidden>
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <span key={i} className={cn('h-2.5 w-2 rounded-[2px]', i < lit ? fill : 'bg-muted')} />
        ))}
      </span>
      <span className="text-sm tabular-nums">{value}%</span>
    </span>
  );
}

/** Status badge; the status note (why it is in Error or Hold) shows on hover and focus. */
export function VehicleStatusBadge({ v }: { v: Pick<VehicleDTO, 'status' | 'statusNote'> }) {
  const s = useVehicleLabels().status(v.status);
  const badge = <Badge tone={s?.tone}>{s?.label ?? '—'}</Badge>;
  if (!v.statusNote) return badge;
  return (
    <Tooltip content={v.statusNote}>
      <span tabIndex={0} className="inline-flex rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${s?.label ?? ''}: ${v.statusNote}`}>
        {badge}
      </span>
    </Tooltip>
  );
}

/** Online / offline as a dotted badge. */
export function OnlineBadge({ connected }: { connected: boolean | null | undefined }) {
  const { t } = useTranslation('operations');
  return (
    <Badge tone={connected ? 'success' : 'neutral'} dot>
      {connected ? t('vehicles.online') : t('vehicles.offline')}
    </Badge>
  );
}
